import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon as MuiListItemIcon,
    Breadcrumbs,
    Link,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, Visibility, VisibilityOff, MoreVert, ArrowBack, TableChart, Search, Inventory } from '@mui/icons-material';
import { getSubCategories, deleteSubCategory } from '../../services/Api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllSubCategories = () => {
    const { categoryId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const categoryName = location.state?.categoryName || 'Category';

    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination and Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedSubCategory, setMenuSelectedSubCategory] = useState(null);

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);

    // Debounce effect: update debouncedSearch after 200ms of no typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0); // Reset to first page on search
        }, 200);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchSubCategories = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getSubCategories(categoryId, page + 1, rowsPerPage, debouncedSearch);
            if (response.success || response.data || Array.isArray(response)) {
                const data = response.data || response;
                const items = Array.isArray(data) ? data : (data.docs || data.subCategories || []);
                setSubCategories(items);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.total || response.totalDocs || data.totalDocs || items.length || 0);
            } else {
                setError(response?.message || 'Failed to fetch subcategories');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching subcategories');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, categoryId, debouncedSearch]);

    const handleMenuOpen = (event, subCategory) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedSubCategory(subCategory);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedSubCategory(null);
    };

    const handleDeleteClick = () => {
        setSelectedSubCategory(menuSelectedSubCategory);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await deleteSubCategory(selectedSubCategory.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchSubCategories();
            } else {
                console.error('Failed to delete subcategory');
            }
        } catch (err) {
            console.error('An error occurred while deleting the subcategory:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/categories');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Categories
                    </Link>
                    <Typography color="text.primary">{categoryName} Subcategories</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            {categoryName} Subcategories
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage items related to the {categoryName} category
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search subcategory..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ 
                                width: 250,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '20px',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton onClick={fetchSubCategories} disabled={loading} sx={{ color: 'primary.main' }}>
                            <Refresh />
                        </IconButton>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate(`/sub-categories/modify/${categoryId}`)}
                            sx={{ px: 4, backgroundColor: 'primary.main' }}
                        >
                            Create Subcategory
                        </Button>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="subcategories table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && subCategories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : subCategories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">
                                            {debouncedSearch ? `No subcategories matching "${debouncedSearch}"` : "No subcategories found for this category"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                subCategories.map((sub) => (
                                    <TableRow key={sub.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>
                                            <Avatar
                                                variant="rounded"
                                                src={sub.image}
                                                sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'text.border' }}
                                            >
                                                <TableChart />
                                            </Avatar>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{sub.name}</TableCell>
                                        <TableCell>{sub.order}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={sub.isActive ? <Visibility /> : <VisibilityOff />}
                                                label={sub.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: sub.isActive ? 'primary.main' : 'grey.400',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    '& .MuiChip-icon': { color: 'white', fontSize: 16 }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, sub)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'text.border',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/sub-categories/modify/${categoryId}/${menuSelectedSubCategory.id}`, { state: { subCategory: menuSelectedSubCategory } });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit
                </MenuItem>
                <MenuItem onClick={() => {
                    navigate(`/sub-categories/${menuSelectedSubCategory.id}/products`, { 
                        state: { 
                            subCategoryName: menuSelectedSubCategory.name, 
                            categoryName: categoryName,
                            categoryId 
                        } 
                    });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Inventory fontSize="small" sx={{ color: 'secondary.main' }} /></MuiListItemIcon>
                    Show Products
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Subcategory?"
                message={`Are you sure you want to delete "${selectedSubCategory?.name}"? This action cannot be undone.`}
            />
        </Box>
    );
};

export default GetAllSubCategories;
